/**
 * Helper central para permissões de visibilidade de Ticket no FRONTEND.
 *
 * Problema que resolve:
 *   O backend emite eventos de socket "ticket" (create/update/delete) para a
 *   sala de status da empresa (salaPorEmpresa(companyId, status)) para
 *   garantir atualização em tempo real para admin/supervisor e para todos
 *   da equipe. O problema: no primeiro load via HTTP GET /tickets a lista
 *   já vem 100% filtrada por dono/fila/perfil, mas se um atendente comum
 *   (perfil "user") recebe por socket um ticket de OUTRO atendente
 *   (ex.: Stephani Silva responde, evento ticket "update" dispara para a
 *   sala), o reducer de UPDATE_TICKET do TicketsList faz "unshift" se o
 *   ticket não existia na lista local — aparecendo para quem NÃO é dono
 *   (bug chato: some no F5 e volta quando o colega mexe).
 *
 * Solução NÃO INVASIVA ("sem quebrar nada"):
 *   NÃO mudamos nada no backend (que tem ~50 locais espalhados fazendo
 *   `io.to(salaPorEmpresa).emit("ticket")`). Criamos este helper central
 *   e chamamos em TODOS os listener `socket.on("ticket")` antes de qualquer
 *   dispatch de atualização de lista — defesa em profundidade.
 *
 * Regras VERBATIM do usuário:
 *   • Administradores (profile = "admin") → VÊ TUDO. ✅ (certo hoje).
 *   • Supervisores (profile = "supervisor") → VÊ TUDO. ✅ (certo hoje).
 *   • Usuário atendente comum (profile = "user") → SÓ PODE VER:
 *       (a) tickets SEM DONO (userId null) — esperando atendimento na fila;
 *       (b) tickets COM userId IGUAL ao seu id — atribuídos a ele;
 *       (c) tickets com MENÇÃO ATIVA para ele (nota interna @mention);
 *       CASO CONTRÁRIO: Bloqueia e NÃO insere/atualiza na lista.
 *
 *   Qualquer outra regra de negócio (status da aba, fila, periodo, busca,
 *   tags, grupos, favoritos, visão "minhas"/"setor", etc) CONTINUA sendo
 *   tratada no componente dono da lista (shouldUpdateTicket / reducer etc)
 *   — este helper só resolve a quebra inter-atendentes.
 */

/**
 * Retorna true se o ticket tem menção ATIVA para o usuário logado (ainda não
 * dispensou explicitamente). Neste caso, ele pode ver o ticket MESMO que
 * não seja o dono.
 */
export function temMencaoAtivaUsuario(ticket) {
  if (!ticket) return false;
  return (
    ticket.hasMentionForUser === true ||
    ticket.hasUnreadMentionForUser === true ||
    Number(ticket.unreadMentions || 0) > 0
  );
}

function normalizarPerfil(profile) {
  return String(profile || "").toLowerCase();
}

function escopoEmpresa(perfilNormalizado) {
  return perfilNormalizado === "admin" || perfilNormalizado === "supervisor";
}

/**
 * Retorna true se o usuário é admin OU supervisor (escopo empresa).
 * Usado para bloquear ações destrutivas (excluir contato/ticket etc) para
 * atendentes comuns.
 */
export function ehAdminOuSupervisor(user) {
  if (!user) return false;
  return escopoEmpresa(normalizarPerfil(user.profile));
}

/**
 * Retorna true se o usuário tem permissão de VISUALIZAR o ticket (ignorando
 * menção ativa — considere usar usuarioPodeVerOuMencao abaixo).
 */
export function usuarioPodeVerTicket(user, ticket) {
  if (!user || !ticket) return false;
  const perfil = normalizarPerfil(user.profile);
  // Admin / Supervisor: sempre passa (escopo empresa).
  if (escopoEmpresa(perfil)) return true;
  // Atendente comum. Não pode ver tickets atribuídos a OUTRO colega.
  const ticketUserId = Number(ticket.userId || 0);
  const meuId = Number(user.id || 0);
  if (ticketUserId > 0 && ticketUserId !== meuId) {
    return false;
  }
  return true;
}

/**
 * Combina as duas regras: "menção ativa passa sempre" OR "permissão de dono".
 * Chamar esta em 99% dos listeners de socket antes do dispatch.
 */
export function usuarioPodeVerOuMencao(user, ticket) {
  if (temMencaoAtivaUsuario(ticket)) return true;
  return usuarioPodeVerTicket(user, ticket);
}

export default {
  temMencaoAtivaUsuario,
  usuarioPodeVerTicket,
  usuarioPodeVerOuMencao,
};
